import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "ellipsis text-subtitle2" }
const _hoisted_3 = {
  key: 0,
  class: "sfx-card__top-right-label"
}
const _hoisted_4 = { class: "text-h4" }
const _hoisted_5 = { class: "row" }
const _hoisted_6 = { class: "col" }
const _hoisted_7 = {
  key: 0,
  class: "sfx-card__icon-row-margin self-end q-mt-lg"
}

import { date } from "quasar"
import SfxBtnIcon from "src/components/button/SfxBtnIcon.vue"
import SfxCard from "src/components/card/SfxCard.vue"
import SfxChip from "src/components/data-point/SfxChip.vue"
import SfxDataPoint from "src/components/data-point/SfxDataPoint.vue"
import SfxIconRow from "src/components/special/SfxIconRow.vue"
import SfxProgressIndicator from "src/components/special/SfxProgressIndicator.vue"
import { useCacheStore } from "src/stores/cacheStore"
import { SFX_CARD_CONDENSED_HEIGHT } from "src/util/constants/configurations"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { getTemporalInfo } from "src/util/functions/time"
import { getProgressIndicatorStateLabel } from "src/util/functions/workOrder"
import { getWorkOrderPriorityColor } from "src/util/functions/workOrderPriority"
import { IconRowElement } from "src/util/types/components/sfxIconRow"
import { WorkOrderCardData } from "src/util/types/dataModels/workOrder"
import { computed, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxCardWorkOrderCondensed',
  props: {
  workOrder: {
    type: Object as PropType<WorkOrderCardData>,
    required: true,
  },

  closable: {
    type: Boolean,
    default: () => false,
  },

  clickable: {
    type: Boolean,
    default: () => true,
  },
},
  emits: ["clickClose"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const { t } = useI18n()
const cacheStore = useCacheStore()

const deadLineLabel = t("sfxCard.deadline")
const chipOverdueLabel = t("time.generic_overdue")
const chipBehindLabel = t("time.generic_behind")

const storeWorkOrder = ref<WorkOrderCardData>()
const workDomainRaw = ref(props.workOrder?.workDomainData)
const workOrderIcons = ref(props.workOrder?.icons)

const workDomainDesignation = computed(() => workDomainRaw.value?.designation)

const deadline = computed(() =>
  props.workOrder?.workOrderData.deadline_dt !== null
    ? getTemporalInfo(props.workOrder?.workOrderData.deadline_dt, "deadline")
    : undefined
)

const cardClasses = computed<string[]>(() => {
  const classes = []

  if (isOverdue.value) {
    classes.push("sfx-card-work-order__overdue")
  } else if (isBehind.value) {
    classes.push("sfx-card-work-order__behind")
  }

  return classes
})

// We already have the item list in regular work, but we need to manually fetch the data for corrective
// This is why the total # is gotten from different places.
const totalCount = computed<number>(() =>
  props.workOrder?.isCorrectiveWork
    ? props.workOrder.issuesData.length
    : props.workOrder.workOrderData.items.length
)

// The passed work list has already been filtered for rejected work
const workDone = computed<number>(() => props.workOrder?.performedWorkData.length)

const state = computed(() =>
  getProgressIndicatorStateLabel(workDone.value, totalCount.value, props.workOrder?.workOrderData)
)

const isOverdue = ref<boolean>(false)
const isBehind = ref<boolean>(false)
const timeWarningLabel = computed(() => isOverdue.value || isBehind.value)

const isClosed = computed(() => state.value === "closed" || state.value === "complete_closed")

//endregion

//region Functions

function assignData(workOrder: WorkOrderCardData) {
  resetData()

  storeWorkOrder.value = cacheStore.workOrderCard.get(workOrder.workOrderData.id)

  assignWorkDomain(workOrder)
  assignWorkOrderIcons(workOrder)
  assignTimeWarning(workOrder)
}

function resetData() {
  workOrderIcons.value = undefined
  workDomainRaw.value = undefined
  isOverdue.value = false
  isBehind.value = false
}

function assignWorkDomain(workOrder: WorkOrderCardData) {
  // We already have the data so skip it
  if (workDomainRaw.value) return

  const workDomain = cacheStore.work_domain_workspace.get(workOrder.workOrderData.work_domain)

  workDomainRaw.value = workDomain

  if (storeWorkOrder.value) storeWorkOrder.value.workDomainData = workDomain
}

async function assignWorkOrderIcons(workOrder: WorkOrderCardData) {
  if (workOrderIcons.value !== undefined) return

  const iconRow: IconRowElement[] = []

  if (state.value === "complete" || state.value === "complete_closed") {
    iconRow.push({ name: Icons.ACCEPT, color: "positive" })
  } else {
    //work order priority icon, use key for hex color to be dynamic
    iconRow.push({
      name: Icons.WORK_ORDERS,
      color: getWorkOrderPriorityColor(workOrder.priorityData),
    })
  }

  iconRow.push({
    name: workOrder.isCorrectiveWork ? Icons.WORK_CORRECTIVE : Icons.WORK_REGULAR,
    color: "fun-label-and-icon",
  })

  // We don't add the entity icon for item or area, since the condensed cards are only going to be used
  // in either of those screens and the relation would be obvious

  workOrderIcons.value = iconRow

  if (storeWorkOrder.value) storeWorkOrder.value.icons = iconRow
}

function assignTimeWarning(workOrder: WorkOrderCardData) {
  const deadline = workOrder.workOrderData.deadline_dt
  if (
    deadline === null ||
    state.value === "complete" ||
    state.value === "closed" ||
    state.value === "complete_closed"
  )
    return

  if (Date.now() >= new Date(deadline).getTime()) {
    isOverdue.value = true
  } else {
    const workPerDay =
      totalCount.value / date.getDateDiff(deadline, workOrder.workOrderData.start_dt, "days")
    const daysWorked = date.getDateDiff(workOrder.workOrderData.start_dt, Date.now(), "days")

    const expectedWorkDone = Math.floor(workPerDay * daysWorked)

    if (expectedWorkDone < workDone.value) {
      isBehind.value = true
    }
  }
}

function handleClose() {
  emit("clickClose")
}

//endregion

//region Setup Code

watch(
  () => props.workOrder,
  async (newWorkOrder) => {
    assignData(newWorkOrder)
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_space = _resolveComponent("q-space")!
  const _directive_truncate_middle = _resolveDirective("truncate-middle")!

  return (_openBlock(), _createBlock(SfxCard, {
    class: _normalizeClass(["sfx-card-work-order q-my-md", cardClasses.value]),
    closed: isClosed.value,
    clickable: props.clickable,
    height: _unref(SFX_CARD_CONDENSED_HEIGHT)
  }, {
    graphics: _withCtx(() => [
      _createVNode(SfxProgressIndicator, {
        current: workDone.value,
        total: totalCount.value,
        state: state.value,
        mode: "condensed"
      }, null, 8, ["current", "total", "state"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("span", _hoisted_2, _toDisplayString(workDomainDesignation.value), 1),
        _createVNode(_component_q_space),
        (timeWarningLabel.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
              (isOverdue.value)
                ? (_openBlock(), _createBlock(SfxChip, {
                    key: 0,
                    label: _unref(chipOverdueLabel),
                    "icon-right": "warning",
                    color: "negative",
                    "text-color": "white"
                  }, null, 8, ["label"]))
                : (isBehind.value)
                  ? (_openBlock(), _createBlock(SfxChip, {
                      key: 1,
                      label: _unref(chipBehindLabel),
                      "icon-right": "turtle",
                      color: "warning",
                      "text-color": "white"
                    }, null, 8, ["label"]))
                  : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (props.closable)
          ? (_openBlock(), _createBlock(SfxBtnIcon, {
              key: 1,
              icon: _unref(Icons).CLOSE,
              onClick: _withModifiers(handleClose, ["exact","stop"]),
              padding: "none",
              class: "sfx-card__top-right-close-button"
            }, null, 8, ["icon"]))
          : _createCommentVNode("", true)
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_4, null, 512), [
        [_directive_truncate_middle, props.workOrder?.workOrderData?.designation]
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          (deadline.value)
            ? (_openBlock(), _createBlock(SfxDataPoint, {
                key: 0,
                label: _unref(deadLineLabel),
                "card-field": ""
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(deadline.value), 1)
                ]),
                _: 1
              }, 8, ["label"]))
            : _createCommentVNode("", true)
        ]),
        (workOrderIcons.value?.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(SfxIconRow, { icons: workOrderIcons.value }, null, 8, ["icons"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["class", "closed", "clickable", "height"]))
}
}

})